/**
 * Rounded, color-coded attendance status control for participant rows
 * (appointment header table and edit-modal participant cards).
 */
import { Badge, Select, type SelectProps } from '@mantine/core';
import {
  buildCalendarStatusSelectData,
} from '@/pages/appointments/utils/appointmentWorkflowStatus';
import {
  getParticipantAttendanceStatusLabel,
  PARTICIPANT_ATTENDANCE_STATUS_OPTIONS,
  type ParticipantAttendanceFhirStatus,
} from '@/pages/appointments/utils/participantAttendance';
import { participantAttendancePillColorsForStatus } from './participantAttendanceSelectStyles';

export interface ParticipantAttendanceSelectProps {
  value: ParticipantAttendanceFhirStatus | string;
  onChange?: (status: ParticipantAttendanceFhirStatus) => void;
  /** Baseline saved status — drives disabled transition options. */
  savedStatus?: ParticipantAttendanceFhirStatus | string;
  size?: SelectProps['size'];
  /** When true, render a static pill (no dropdown). */
  readOnly?: boolean;
  'aria-label'?: string;
  comboboxProps?: SelectProps['comboboxProps'];
}

function pillSelectStyles(status: string | null | undefined): SelectProps['styles'] {
  const colors = participantAttendancePillColorsForStatus(status);
  return {
    input: {
      backgroundColor: colors.backgroundColor,
      color: colors.color,
      border: 'none',
      fontWeight: 500,
      borderRadius: 9999,
      minHeight: 32,
      paddingInline: 12,
    },
    section: {
      color: colors.color,
    },
  };
}

export function ParticipantAttendanceSelect({
  value,
  onChange,
  savedStatus,
  size = 'sm',
  readOnly = false,
  'aria-label': ariaLabel,
  comboboxProps,
}: ParticipantAttendanceSelectProps): JSX.Element {
  const saved = savedStatus ?? value;
  const selectData = buildCalendarStatusSelectData(
    saved,
    PARTICIPANT_ATTENDANCE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  );
  const colors = participantAttendancePillColorsForStatus(value);

  if (readOnly || !onChange) {
    return (
      <Badge
        variant="light"
        size={size === 'xs' ? 'sm' : 'md'}
        radius="xl"
        styles={{
          root: {
            backgroundColor: colors.backgroundColor,
            color: colors.color,
            textTransform: 'none',
            fontWeight: 500,
            border: 'none',
          },
        }}
      >
        {getParticipantAttendanceStatusLabel(value)}
      </Badge>
    );
  }

  return (
    <Select
      size={size}
      data={selectData}
      value={value}
      onChange={(next) => {
        if (!next) return;
        onChange(next as ParticipantAttendanceFhirStatus);
      }}
      allowDeselect={false}
      aria-label={ariaLabel}
      comboboxProps={comboboxProps}
      styles={pillSelectStyles(value)}
    />
  );
}
